import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts'
import styled from 'styled-components'
import {
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  ListSubheader,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getInventory, getTeams, getYardById, getYardHives, getYards, getWeightRecordsByYard } from '../services/api'
import { getTasks } from '../services/tasks'
import { createTreatment, getTreatments, Treatment } from '../services/treatments'
import { createInspection, getInspections, getInspectionStats, Inspection } from '../services/inspections'
import { createHarvest, getHarvests, Harvest } from '../services/harvests'
import { createMovement, getMovements, Movement } from '../services/movements'
import { getUsers } from '../services/users'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/common/LoadingSpinner'
import YardPhotoJournal from '../components/yards/YardPhotoJournal'

const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
  padding: 0 ${props => props.theme.spacing.sm};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${props => props.theme.spacing.xl};
`

const Section = styled.div`
  margin-top: ${props => props.theme.spacing.xl};
`

const SectionTitle = styled.h2`
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  font-size: ${props => props.theme.typography.fontSize.xlarge};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
`

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: ${props => props.theme.spacing.md};

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const KPIBox = styled.div<{ $bg?: string; $border?: string }>`
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.large};
  background: ${props => props.$bg || props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.$border || props.theme.colors.border};
  text-align: center;
`

const ActionsRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.xl};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const DetailsCard = styled(Card)`
  border-radius: ${props => props.theme.borderRadius.large} !important;
  border: 1px solid ${props => props.theme.colors.border};
`

const ActivityCard = styled(Card)`
  border-radius: ${props => props.theme.borderRadius.large} !important;
  border: 1px solid ${props => props.theme.colors.border};
`

const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
`

const DetailLabel = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
`

const DetailValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`

const AddressText = styled.span`
  color: ${props => props.theme.colors.text};
`

const ListCard = styled.div`
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.large};
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`

const ListItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`


/**
 * Treatment type values are stored in English (API data); map each known value
 * to its translation key so the label is translated at render time.
 */
const TREATMENT_TYPE_KEYS: Record<string, string> = {
  'Treat for Varroa Mites': 'yardDetails.taskTypes.treatVarroaMites',
  'Feed Sugar Syrup': 'yardDetails.taskTypes.feedSugarSyrup',
  'Feed Pollen Substitute': 'yardDetails.taskTypes.feedPollenSubstitute',
  'Requeen Hive': 'yardDetails.taskTypes.reqeenHive',
  'Replace Old Frames': 'yardDetails.taskTypes.replaceOldFrames',
  'Clean Hive Equipment': 'yardDetails.taskTypes.cleanHiveEquipment',
}


/** Israel Honey Board pollination-hive minimum (Mar–Dec): occupied frames / brood frames per hive. */
const POLLINATION_STANDARD = { occupied: 10, brood: 7 }

interface Yard {
  _id?: string
  id?: string
  name: string
  address: string
  latitude?: number
  longitude?: number
  gateCode?: string
  population?: number
  temperature?: number
  lastVisitDate?: string
  lastTreatmentDate?: string
  lastTeamId?: number
}

interface Warehouse {
  _id?: string
  warehouseId?: number
  description?: string
}

interface Hive {
  id?: string
  status?: 'active' | 'inactive' | 'dead'
  type?: 'small' | 'medium' | 'large'
}

interface HiveGroups {
  small?: Hive[]
  medium?: Hive[]
  large?: Hive[]
}

function YardDetailsScreen(): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const navigate = useNavigate()
  const { yardId } = useParams()
  const { user } = useAuth()
  const [yard, setYard] = useState<Yard | null>(null)
  const [loading, setLoading] = useState(true)
  const [hiveGroups, setHiveGroups] = useState<HiveGroups>({})
  const [treatments, setTreatments] = useState<Treatment[]>([])
  const [tasks, setTasks] = useState<any[]>([])
  const [inspections, setInspections] = useState<Inspection[]>([])
  const [harvests, setHarvests] = useState<Harvest[]>([])
  const [movements, setMovements] = useState<Movement[]>([])
  const [teams, setTeams] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [allYards, setAllYards] = useState<Yard[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [inspectOpen, setInspectOpen] = useState(false)
  const [treatmentOpen, setTreatmentOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)
  const [harvestOpen, setHarvestOpen] = useState(false)
  const [weightRecords, setWeightRecords] = useState<any[]>([])
  const [activityTab, setActivityTab] = useState<'activity' | 'tasks'>('activity')
  const [moveDestinationId, setMoveDestinationId] = useState('')
  const [inspectionDate, setInspectionDate] = useState('')
  const [inspectionInspector, setInspectionInspector] = useState('')
  const [inspectionNotes, setInspectionNotes] = useState('')
  const [inspectionVeryGood, setInspectionVeryGood] = useState('')
  const [inspectionGood, setInspectionGood] = useState('')
  const [inspectionNormal, setInspectionNormal] = useState('')
  const [inspectionBad, setInspectionBad] = useState('')
  const [inspectionPopulation, setInspectionPopulation] = useState('')
  const [inspectionBrood, setInspectionBrood] = useState('')
  const [inspectionHasFlowering, setInspectionHasFlowering] = useState(false)
  const [treatmentType, setTreatmentType] = useState('Treat for Varroa Mites')
  const [treatmentCustomType, setTreatmentCustomType] = useState('')
  const [treatmentDate, setTreatmentDate] = useState('')
  const [treatmentRecipeId, setTreatmentRecipeId] = useState('')
  const [treatmentNotes, setTreatmentNotes] = useState('')
  const [moveBoxType, setMoveBoxType] = useState<'deeps' | 'super' | 'shallow'>('deeps')
  const [moveQuantity, setMoveQuantity] = useState('')
  const [moveDate, setMoveDate] = useState('')
  const [harvestDate, setHarvestDate] = useState('')
  const [harvestHoneyType, setHarvestHoneyType] = useState('wildflower')
  const [harvestFormat, setHarvestFormat] = useState('barrel')
  const [harvestQuantity, setHarvestQuantity] = useState('')
  const [harvestNotes, setHarvestNotes] = useState('')
  const [inspectionStats, setInspectionStats] = useState<{
    veryGood: number
    good: number
    normal: number
    bad: number
    total: number
    lastInspectionDate: string | null
    hasFlowering?: boolean
    notes?: string
  }>({
    veryGood: 0,
    good: 0,
    normal: 0,
    bad: 0,
    total: 0,
    lastInspectionDate: null,
  })

  const yardKey = yard?.id || yard?._id

  const hiveCounts = useMemo(() => {
    const hives = [
      ...(hiveGroups.small || []),
      ...(hiveGroups.medium || []),
      ...(hiveGroups.large || []),
    ]
    const active = hives.filter(h => h.status === 'active').length
    const inactive = hives.filter(h => h.status === 'inactive').length
    const dead = hives.filter(h => h.status === 'dead').length
    const total = hives.length
    return {
      total,
      veryGood: active,
      good: inactive,
      normal: 0,
      bad: dead,
    }
  }, [hiveGroups])

  useEffect(() => {
    async function loadYard(): Promise<void> {
      try {
        if (!yardId) return
        const [
          yardRes,
          hivesRes,
          treatmentsRes,
          tasksRes,
          teamsRes,
          yardsRes,
          inventoryRes,
          inspectionsRes,
          harvestsRes,
          movementsRes,
          usersRes,
        ] = await Promise.all([
          getYardById(yardId),
          getYardHives(yardId),
          getTreatments(),
          getTasks(),
          getTeams(),
          getYards(),
          getInventory(),
          getInspections({ yardId }),
          getHarvests({ yardId }),
          getMovements({ yardId }),
          getUsers(),
        ])
        const yardData = yardRes.data
        setYard(yardData)
        setHiveGroups(hivesRes.data || {})
        setTeams(teamsRes.data || [])
        setAllYards(yardsRes.data || [])
        setWarehouses(
          (inventoryRes.data || []).filter(
            (item: Warehouse) => item.warehouseId || item._id
          )
        )

        const filteredTreatments = (treatmentsRes.data || []).filter(treatment =>
          String(treatment.yardId) === String(yardData.id || yardData._id)
        )
        setTreatments(filteredTreatments)
        setInspections(inspectionsRes.data || [])
        setHarvests(harvestsRes.data || [])
        setMovements(movementsRes.data || [])
        setUsers(usersRes.data || [])

        // Filter tasks by yardId
        const filteredTasks = (tasksRes.data || []).filter((task: any) => {
          return String(task.yardId) === String(yardData.id || yardData._id)
        })
        setTasks(filteredTasks)

        // Load weight records
        try {
          const weightRes = await getWeightRecordsByYard(String(yardData.id || yardData._id))
          setWeightRecords(weightRes.data || [])
        } catch { setWeightRecords([]) }

        // Load inspection statistics
        try {
          const statsRes = await getInspectionStats(String(yardData.id || yardData._id))
          setInspectionStats(statsRes.data)
        } catch (error) {
          console.error('Error loading inspection stats:', error)
        }
      } catch (error) {
        console.error('Error loading yard details:', error)
      } finally {
        setLoading(false)
      }
    }

    loadYard()
  }, [yardId])
  const latestTreatmentDate = useMemo(() => {
    const dates = treatments
      .map(treatment => new Date(treatment.treatmentDate))
      .filter(date => !Number.isNaN(date.getTime()))
    if (!dates.length) return null
    return new Date(Math.max(...dates.map(date => date.getTime())))
  }, [treatments])

  const latestTaskDate = useMemo(() => {
    const dates = tasks
      .map(task => new Date(task.dueDate))
      .filter(date => !Number.isNaN(date.getTime()))
    if (!dates.length) return null
    return new Date(Math.max(...dates.map(date => date.getTime())))
  }, [tasks])

  const upcomingTasks = useMemo(() => {
    const now = new Date()
    return tasks
      .filter(task => {
        const dueDate = new Date(task.dueDate)
        return task.status !== 'done' && dueDate >= now
      })
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
  }, [tasks])

  const latestInspectionDate = useMemo(() => {
    const dates = inspections
      .map(inspection => new Date(inspection.inspectionDate))
      .filter(date => !Number.isNaN(date.getTime()))
    if (!dates.length) return null
    return new Date(Math.max(...dates.map(date => date.getTime())))
  }, [inspections])

  const latestInspection = useMemo(() => {
    if (!inspections.length) return null
    return inspections
      .slice()
      .sort(
        (a, b) =>
          new Date(b.inspectionDate).getTime() -
          new Date(a.inspectionDate).getTime()
      )[0]
  }, [inspections])

  const kpiCounts = useMemo(() => {
    // Prefer inspection stats from API (which updates from voice recordings)
    if (inspectionStats.total > 0) {
      return {
        veryGood: inspectionStats.veryGood,
        good: inspectionStats.good,
        normal: inspectionStats.normal,
        bad: inspectionStats.bad,
        total: inspectionStats.total,
      }
    }

    // Fallback to latest inspection from list
    const hasInspectionKpis = Boolean(
      latestInspection &&
        (latestInspection.veryGoodCount !== undefined ||
          latestInspection.goodCount !== undefined ||
          latestInspection.normalCount !== undefined ||
          latestInspection.badCount !== undefined)
    )
    if (!hasInspectionKpis) return hiveCounts
    const veryGood = latestInspection?.veryGoodCount ?? 0
    const good = latestInspection?.goodCount ?? 0
    const normal = latestInspection?.normalCount ?? 0
    const bad = latestInspection?.badCount ?? 0
    const total = veryGood + good + normal + bad
    return { total, veryGood, good, normal, bad }
  }, [inspectionStats, latestInspection, hiveCounts])

  const hiveTypeCounts = useMemo(() => {
    return {
      small: hiveGroups.small?.length || 0,
      medium: hiveGroups.medium?.length || 0,
      large: hiveGroups.large?.length || 0,
    }
  }, [hiveGroups])

  const supersCount = useMemo(() => {
    if (!yardKey) return 0
    return movements.reduce((total, movement) => {
      if (movement.boxType !== 'super') return total
      const fromMatches = String(movement.fromYardId) === String(yardKey)
      const toMatches = movement.destinationType === 'yard' &&
        String(movement.toYardId) === String(yardKey)
      if (toMatches) return total + movement.quantity
      if (fromMatches) return total - movement.quantity
      return total
    }, 0)
  }, [movements, yardKey])

  const lastTeamName = useMemo(() => {
    if (!yard?.lastTeamId) return '-'
    const teamMatch = teams.find(
      team => String(team.id || team._id) === String(yard.lastTeamId)
    )
    return teamMatch?.name || t('yard.teamFallback', { id: yard.lastTeamId })
  }, [teams, yard, t])

  const weightChartData = useMemo(() => {
    return weightRecords.map((r: any) => ({
      date: new Date(r.date).toLocaleDateString(locale),
      weight: r.weight,
    }))
  }, [weightRecords, locale])

  // Population & brood trend over time (from inspections)
  // Frames per hive (Israel Honey Board terms): one point per day = the average over that
  // day's inspections, on a real time axis. Values above MAX_FRAMES_PER_HIVE are legacy
  // records where a hive count was typed into this field, so they're left out.
  const populationChartData = useMemo(() => {
    const MAX_FRAMES_PER_HIVE = 20
    const valid = (v: number | null | undefined) =>
      v != null && Number.isFinite(v) && v >= 0 && v <= MAX_FRAMES_PER_HIVE ? v : null
    const byDay = new Map<number, { pop: number[]; brood: number[] }>()
    for (const inspection of inspections) {
      const d = new Date(inspection.inspectionDate)
      if (Number.isNaN(d.getTime())) continue
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
      const bucket = byDay.get(day) ?? { pop: [], brood: [] }
      const pop = valid(inspection.population ?? inspection.populationCount)
      const brood = valid(inspection.brood)
      if (pop != null) bucket.pop.push(pop)
      if (brood != null) bucket.brood.push(brood)
      byDay.set(day, bucket)
    }
    const avg = (xs: number[]) => (xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : null)
    return Array.from(byDay.entries())
      .map(([time, b]) => ({ time, population: avg(b.pop), brood: avg(b.brood) }))
      .filter(point => point.population != null || point.brood != null)
      .sort((a, b) => a.time - b.time)
  }, [inspections])
  const formatChartDay = useCallback(
    (time: number) => new Date(time).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
    [locale],
  )

  // How many hives these charts represent (yard hive count)
  const representedHives = kpiCounts.total || yard?.population || 0

  const activityLogs = useMemo(() => {
    const treatmentLogs = treatments.map(treatment => ({
      id: `treatment-${treatment.id}`,
      title: treatmentTypeLabel(treatment.treatmentType),
      date: treatment.treatmentDate,
      description: treatment.notes,
      userId: (treatment as any).userId,
      type: 'treatment',
    }))
    const inspectionLogs = inspections.map(inspection => ({
      id: `inspection-${inspection.id}`,
      title: t('yard.activity.inspection'),
      date: inspection.inspectionDate,
      description: inspection.notes,
      userId: inspection.userId,
      type: 'inspection',
    }))
    const harvestLogs = harvests.map(harvest => ({
      id: `harvest-${harvest.id}`,
      title: t('yard.activity.harvest'),
      date: harvest.harvestDate,
      description: harvest.notes,
      userId: (harvest as any).userId,
      type: 'harvest',
    }))
    const movementLogs = movements.map(movement => ({
      id: `movement-${movement.id}`,
      title: t('yard.activity.movement'),
      date: movement.movementDate || movement.createdAt || '',
      description: t('yard.activity.movementDescription', {
        quantity: movement.quantity,
        boxType: t(`inventory.boxTypes.${movement.boxType}`, { defaultValue: movement.boxType }),
      }),
      userId: (movement as any).userId,
      type: 'move',
    }))
    const taskLogs = tasks.map(task => ({
      id: `task-${task.id}`,
      title: task.title,
      date: task.dueDate,
      description: taskStatusLabel(task.status),
      userId: task.userId || task.createdBy,
      type: 'task',
    }))
    return [...treatmentLogs, ...inspectionLogs, ...harvestLogs, ...movementLogs, ...taskLogs]
      .filter(log => log.date)
      .sort(
        (a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatments, inspections, harvests, movements, tasks, t])

  const userNameById = useMemo(() => {
    const map = new Map<string, string>()
    users.forEach((item: any) => {
      const key = String(item.id || item._id)
      map.set(key, item.name)
    })
    return map
  }, [users])

  /**
   * Localized label for a stored (English) treatment type; custom types pass through
   */
  function treatmentTypeLabel(value: string): string {
    const key = TREATMENT_TYPE_KEYS[value]
    return key ? t(key) : value
  }

  /**
   * Localized label for a task status (open / in_progress / done)
   */
  function taskStatusLabel(status?: string): string {
    if (!status) return ''
    return t(`tasks.status.${status}`, { defaultValue: status })
  }

  async function handleSaveInspection(): Promise<void> {
    if (!yardKey || !inspectionDate) return
    const inspectorPrefix = inspectionInspector
      ? t('yard.inspection.inspectorNote', { name: inspectionInspector })
      : ''
    const notes = [inspectorPrefix, inspectionNotes].filter(Boolean).join('\n')
    try {
      const response = await createInspection({
        yardId: yardKey,
        inspectionDate,
        veryGoodCount: inspectionVeryGood ? Number(inspectionVeryGood) : undefined,
        goodCount: inspectionGood ? Number(inspectionGood) : undefined,
        normalCount: inspectionNormal ? Number(inspectionNormal) : undefined,
        badCount: inspectionBad ? Number(inspectionBad) : undefined,
        population: inspectionPopulation ? Number(inspectionPopulation) : undefined,
        brood: inspectionBrood ? Number(inspectionBrood) : undefined,
        hasFlowering: inspectionHasFlowering,
        notes: notes || undefined,
        userId: user?.id,
      })
      setInspections(prev => [response.data, ...prev])
      setInspectOpen(false)
      setInspectionDate('')
      setInspectionInspector('')
      setInspectionNotes('')
      setInspectionVeryGood('')
      setInspectionGood('')
      setInspectionNormal('')
      setInspectionBad('')
      setInspectionPopulation('')
      setInspectionBrood('')
      setInspectionHasFlowering(false)
    } catch (error) {
      console.error('Error saving inspection:', error)
    }
  }

  async function handleSaveTreatment(): Promise<void> {
    const resolvedType = treatmentType === 'custom' ? treatmentCustomType : treatmentType
    if (!yardKey || !treatmentDate || !resolvedType) return
    try {
      const response = await createTreatment({
        yardId: yardKey,
        treatmentType: resolvedType,
        treatmentDate,
        recipeId: treatmentRecipeId ? Number(treatmentRecipeId) : undefined,
        notes: treatmentNotes || undefined,
        userId: user?.id,
      })
      setTreatments(prev => [response.data, ...prev])
      setTreatmentOpen(false)
      setTreatmentDate('')
      setTreatmentNotes('')
      setTreatmentRecipeId('')
      setTreatmentCustomType('')
    } catch (error) {
      console.error('Error saving treatment:', error)
    }
  }

  async function handleSaveMovement(): Promise<void> {
    if (!yardKey || !moveDestinationId || !moveQuantity) return
    const [destinationType, destinationId] = moveDestinationId.split(':')
    try {
      const response = await createMovement({
        fromYardId: yardKey,
        destinationType: destinationType as 'yard' | 'warehouse',
        toYardId: destinationType === 'yard' ? destinationId : undefined,
        toWarehouseId: destinationType === 'warehouse' ? destinationId : undefined,
        movementType: 'transfer',
        boxType: moveBoxType,
        quantity: Number(moveQuantity),
        movementDate: moveDate || undefined,
        userId: user?.id,
      })
      setMovements(prev => [response.data, ...prev])
      setMoveOpen(false)
      setMoveDestinationId('')
      setMoveQuantity('')
      setMoveDate('')
    } catch (error) {
      console.error('Error saving movement:', error)
    }
  }

  async function handleSaveHarvest(): Promise<void> {
    if (!yardKey || !harvestDate) return
    try {
      const response = await createHarvest({
        yardId: yardKey,
        harvestDate,
        honeyType: harvestHoneyType,
        format: harvestFormat,
        quantity: harvestQuantity ? Number(harvestQuantity) : undefined,
        notes: harvestNotes || undefined,
        userId: user?.id,
      })
      setHarvests(prev => [response.data, ...prev])
      setHarvestOpen(false)
      setHarvestDate('')
      setHarvestQuantity('')
      setHarvestNotes('')
    } catch (error) {
      console.error('Error saving harvest:', error)
    }
  }

  return (
    <Container>
      <Header>
        <Button variant="outlined" onClick={() => navigate('/yards')}>
          {t('button.back.text')}
        </Button>
      </Header>
      {loading ? (
        <LoadingSpinner />
      ) : yard ? (
        <Card>
          <CardContent>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {yard.name}
            </Typography>
            <Section>
              <KPIGrid>
                <KPIBox
                  $bg="#e3f2fd"
                  $border="#bbdefb"
                  style={{ background: '#e3f2fd', border: '1px solid #bbdefb' }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#1976d2' }}>
                    {t('yard.hivesVeryGood')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#1976d2' }}>
                    {kpiCounts.veryGood}
                  </Typography>
                </KPIBox>
                <KPIBox
                  $bg="#e8f5e9"
                  $border="#c8e6c9"
                  style={{ background: '#e8f5e9', border: '1px solid #c8e6c9' }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#2e7d32' }}>
                    {t('yard.hivesGood')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#2e7d32' }}>
                    {kpiCounts.good}
                  </Typography>
                </KPIBox>
                <KPIBox
                  $bg="#fff3e0"
                  $border="#ffe0b2"
                  style={{ background: '#fff3e0', border: '1px solid #ffe0b2' }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#ed6c02' }}>
                    {t('yard.hivesNormal')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#ed6c02' }}>
                    {kpiCounts.normal}
                  </Typography>
                </KPIBox>
                <KPIBox
                  $bg="#fbe9e7"
                  $border="#ffccbc"
                  style={{ background: '#fbe9e7', border: '1px solid #ffccbc' }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#d32f2f' }}>
                    {t('yard.hivesBad')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#d32f2f' }}>
                    {kpiCounts.bad}
                  </Typography>
                </KPIBox>
              </KPIGrid>
              <ActionsRow>
                <Button variant="contained" onClick={() => setInspectOpen(true)}>
                  {t('yard.actions.inspect')}
                </Button>
                <Button variant="outlined" onClick={() => setTreatmentOpen(true)}>
                  {t('yard.actions.treatment')}
                </Button>
                <Button variant="outlined" onClick={() => setMoveOpen(true)}>
                  {t('yard.actions.move')}
                </Button>
                <Button variant="outlined" onClick={() => setHarvestOpen(true)}>
                  {t('yard.actions.harvest')}
                </Button>
              </ActionsRow>
            </Section>

            <ContentGrid>
              <DetailsCard>
                <CardContent>
                  <SectionTitle>{t('yard.details')}</SectionTitle>
                  <DetailsList>
                    <DetailRow>
                      <DetailLabel>{t('yard.address')}</DetailLabel>
                      <AddressRow>
                        <AddressText>{yard.address}</AddressText>
                        <IconButton
                          size="small"
                          onClick={() =>
                            yardKey && navigate(`/?yardId=${encodeURIComponent(String(yardKey))}`)
                          }
                          aria-label={t('yard.viewOnMap')}
                        >
                          <LocationOnIcon fontSize="small" />
                        </IconButton>
                      </AddressRow>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('yard.totalHives')}</DetailLabel>
                      <DetailValue>
                        {kpiCounts.total || yard.population || 0}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('hive.deadHives')}</DetailLabel>
                      <DetailValue>{kpiCounts.bad}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('yard.hiveTypes')}</DetailLabel>
                      <DetailValue>
                        {t('yard.hiveTypesValue', {
                          small: hiveTypeCounts.small,
                          medium: hiveTypeCounts.medium,
                          large: hiveTypeCounts.large,
                        })}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('yard.supersCount')}</DetailLabel>
                      <DetailValue>{supersCount}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('yard.lastVisitDate')}</DetailLabel>
                      <DetailValue>
                        {yard.lastVisitDate
                          ? new Date(yard.lastVisitDate).toLocaleDateString(locale)
                          : latestInspectionDate
                          ? latestInspectionDate.toLocaleDateString(locale)
                          : latestTaskDate
                          ? latestTaskDate.toLocaleDateString(locale)
                          : '-'}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('yard.lastTreatmentDate')}</DetailLabel>
                      <DetailValue>
                        {yard.lastTreatmentDate
                          ? new Date(yard.lastTreatmentDate).toLocaleDateString(locale)
                          : latestTreatmentDate
                          ? latestTreatmentDate.toLocaleDateString(locale)
                          : '-'}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('yard.lastTeam')}</DetailLabel>
                      <DetailValue>{lastTeamName}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>{t('yard.temperature')}</DetailLabel>
                      <DetailValue>
                        {yard.temperature !== undefined && yard.temperature !== null
                          ? `${yard.temperature}°C`
                          : '-'}
                      </DetailValue>
                    </DetailRow>
                  </DetailsList>
                </CardContent>
              </DetailsCard>
              <ActivityCard>
                <CardContent>
                  <SectionTitle>{t('yard.activityLogs')}</SectionTitle>
                  <Tabs
                    value={activityTab}
                    onChange={(_, value) => setActivityTab(value)}
                    sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}
                  >
                    <Tab
                      value="activity"
                      label={t('yard.activityLogs')}
                    />
                    <Tab
                      value="tasks"
                      label={t('yard.upcomingTasks')}
                    />
                  </Tabs>
                  <ListCard>
                    {activityTab === 'activity' && activityLogs.length === 0 ? (
                      <Typography variant="body2">
                        {t('yard.noActivity')}
                      </Typography>
                    ) : null}
                    {activityTab === 'activity' && activityLogs.length > 0 &&
                      activityLogs.slice(0, 6).map((log, index) => (
                        <React.Fragment key={log.id || `${log.type}-${index}`}>
                          <ListItem>
                            <Typography variant="subtitle2">{log.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(log.date).toLocaleString(locale)} • {t(`yard.activity.types.${log.type}`)}
                              {log.userId && userNameById.get(String(log.userId))
                                ? ` • ${userNameById.get(String(log.userId))}`
                                : ''}
                            </Typography>
                            {log.description && (
                              <Typography variant="body2">{log.description}</Typography>
                            )}
                          </ListItem>
                          {index < Math.min(activityLogs.length, 6) - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    {activityTab === 'tasks' && upcomingTasks.length === 0 ? (
                      <Typography variant="body2">
                        {t('yard.noTasks')}
                      </Typography>
                    ) : null}
                    {activityTab === 'tasks' && upcomingTasks.length > 0 &&
                      upcomingTasks.slice(0, 6).map((task, index) => (
                        <React.Fragment key={task.id || `${task.title}-${index}`}>
                          <ListItem>
                            <Typography variant="subtitle2">{task.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(task.dueDate).toLocaleString(locale)} • {taskStatusLabel(task.status)}
                              {task.userId && userNameById.get(String(task.userId))
                                ? ` • ${userNameById.get(String(task.userId))}`
                                : ''}
                            </Typography>
                            {task.description && (
                              <Typography variant="body2">{task.description}</Typography>
                            )}
                          </ListItem>
                          {index < Math.min(upcomingTasks.length, 6) - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                  </ListCard>
                </CardContent>
              </ActivityCard>
            </ContentGrid>
            {weightRecords.length > 0 && (
              <Section>
                <SectionTitle>{t('weight.weightTrend')}</SectionTitle>
                <DetailsCard>
                  <CardContent>
                    <div style={{ width: '100%', height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={weightChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="weight" name={t('weight.weight')} stroke="#9c27b0" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </DetailsCard>
              </Section>
            )}
            <Section>
              <SectionTitle>{t('yard.populationBroodTrend')}</SectionTitle>
              <DetailsCard>
                <CardContent>
                  {populationChartData.length > 0 ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('yard.representsHives', {
                          count: representedHives,
                        })}
                        {' · '}{t('yard.framesChartHint')}
                        {!populationChartData.some(point => point.brood != null) && (
                          <> · {t('yard.noBroodYet')}</>
                        )}
                      </Typography>
                      <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={populationChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="time"
                              type="number"
                              scale="time"
                              domain={['dataMin', 'dataMax']}
                              tickFormatter={formatChartDay}
                              minTickGap={24}
                            />
                            <YAxis allowDecimals={false} width={40} domain={[0, (max: number) => Math.max(12, Math.ceil(max))]} />
                            <ReferenceLine y={POLLINATION_STANDARD.occupied} stroke="#f9a825" strokeDasharray="6 4" label={{ value: t('yard.standardOccupied', { n: POLLINATION_STANDARD.occupied }), position: 'insideTopLeft', fontSize: 11, fill: '#b7791f' }} />
                            <ReferenceLine y={POLLINATION_STANDARD.brood} stroke="#8d6e63" strokeDasharray="6 4" label={{ value: t('yard.standardBrood', { n: POLLINATION_STANDARD.brood }), position: 'insideBottomLeft', fontSize: 11, fill: '#6d4c41' }} />
                            <Tooltip labelFormatter={(time: number) => new Date(time).toLocaleDateString(locale)} />
                            <Legend />
                            <Line type="monotone" dataKey="population" name={t('hive.population')} stroke="#f9a825" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                            <Line type="monotone" dataKey="brood" name={t('yard.inspection.brood')} stroke="#8d6e63" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                      {t('yard.noTrendData')}
                    </Typography>
                  )}
                </CardContent>
              </DetailsCard>
            </Section>
            <Section>
              <SectionTitle>
                📷 {t('yardPhotos.title')}
              </SectionTitle>
              <DetailsCard>
                <CardContent>
                  <YardPhotoJournal yardId={String(yardKey)} />
                </CardContent>
              </DetailsCard>
            </Section>
            <Dialog open={inspectOpen} onClose={() => setInspectOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>{t('yard.actions.inspect')}</DialogTitle>
              <DialogContent>
                <TextField
                  label={t('yard.inspection.inspector')}
                  fullWidth
                  margin="dense"
                  value={inspectionInspector}
                  onChange={event => setInspectionInspector(event.target.value)}
                />
                <TextField
                  label={t('yard.inspection.date')}
                  type="date"
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  value={inspectionDate}
                  onChange={event => setInspectionDate(event.target.value)}
                />
                <TextField
                  label={t('yard.inspection.notes')}
                  fullWidth
                  margin="dense"
                  multiline
                  minRows={3}
                  value={inspectionNotes}
                  onChange={event => setInspectionNotes(event.target.value)}
                />
                <TextField
                  label={t('yard.inspection.veryGood')}
                  type="number"
                  fullWidth
                  margin="dense"
                  value={inspectionVeryGood}
                  onChange={event => setInspectionVeryGood(event.target.value)}
                />
                <TextField
                  label={t('yard.inspection.good')}
                  type="number"
                  fullWidth
                  margin="dense"
                  value={inspectionGood}
                  onChange={event => setInspectionGood(event.target.value)}
                />
                <TextField
                  label={t('yard.inspection.normal')}
                  type="number"
                  fullWidth
                  margin="dense"
                  value={inspectionNormal}
                  onChange={event => setInspectionNormal(event.target.value)}
                />
                <TextField
                  label={t('yard.inspection.bad')}
                  type="number"
                  fullWidth
                  margin="dense"
                  value={inspectionBad}
                  onChange={event => setInspectionBad(event.target.value)}
                />
                <TextField
                  label={t('yard.inspection.population')}
                  type="number"
                  helperText={t('yard.framesHelper')}
                  inputProps={{ min: 0, max: 20 }}
                  fullWidth
                  margin="dense"
                  value={inspectionPopulation}
                  onChange={event => setInspectionPopulation(event.target.value)}
                />
                <TextField
                  label={t('yard.inspection.brood')}
                  type="number"
                  helperText={t('yard.broodHelper')}
                  inputProps={{ min: 0, max: 20 }}
                  fullWidth
                  margin="dense"
                  value={inspectionBrood}
                  onChange={event => setInspectionBrood(event.target.value)}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={inspectionHasFlowering}
                      onChange={event => setInspectionHasFlowering(event.target.checked)}
                    />
                  }
                  label={t('yard.inspection.hasFlowering')}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setInspectOpen(false)}>
                  {t('button.cancel.text')}
                </Button>
                <Button variant="contained" onClick={handleSaveInspection}>
                  {t('button.save.text')}
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog open={treatmentOpen} onClose={() => setTreatmentOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>{t('yard.actions.treatment')}</DialogTitle>
              <DialogContent>
                <TextField
                  select
                  label={t('yard.treatment.type')}
                  fullWidth
                  margin="dense"
                  value={treatmentType}
                  onChange={event => setTreatmentType(event.target.value)}
                >
                  {Object.entries(TREATMENT_TYPE_KEYS).map(([value, key]) => (
                    <MenuItem key={value} value={value}>{t(key)}</MenuItem>
                  ))}
                  <MenuItem value="custom">{t('yard.treatment.customOption')}</MenuItem>
                </TextField>
                {treatmentType === 'custom' && (
                  <TextField
                    label={t('yard.treatment.custom')}
                    fullWidth
                    margin="dense"
                    value={treatmentCustomType}
                    onChange={event => setTreatmentCustomType(event.target.value)}
                  />
                )}
                <TextField
                  label={t('yard.treatment.recipeId')}
                  type="number"
                  fullWidth
                  margin="dense"
                  value={treatmentRecipeId}
                  onChange={event => setTreatmentRecipeId(event.target.value)}
                />
                <TextField
                  label={t('yard.treatment.date')}
                  type="date"
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  value={treatmentDate}
                  onChange={event => setTreatmentDate(event.target.value)}
                />
                <TextField
                  label={t('yard.treatment.notes')}
                  fullWidth
                  margin="dense"
                  multiline
                  minRows={3}
                  value={treatmentNotes}
                  onChange={event => setTreatmentNotes(event.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setTreatmentOpen(false)}>
                  {t('button.cancel.text')}
                </Button>
                <Button variant="contained" onClick={handleSaveTreatment}>
                  {t('button.save.text')}
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog open={moveOpen} onClose={() => setMoveOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>{t('yard.actions.move')}</DialogTitle>
              <DialogContent>
                <TextField
                  label={t('yard.move.from')}
                  fullWidth
                  margin="dense"
                  value={yard.name}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  select
                  label={t('yard.move.to')}
                  fullWidth
                  margin="dense"
                  value={moveDestinationId}
                  onChange={event => setMoveDestinationId(event.target.value)}
                >
                  <ListSubheader>{t('yard.move.toYard')}</ListSubheader>
                  {allYards
                    .filter(option => String(option.id || option._id) !== String(yardKey))
                    .map(option => (
                      <MenuItem
                        key={`yard-${option.id || option._id || option.name}`}
                        value={`yard:${option.id || option._id}`}
                      >
                        {option.name}
                      </MenuItem>
                    ))}
                  <ListSubheader>{t('yard.move.toWarehouse')}</ListSubheader>
                  {warehouses.map(option => (
                    <MenuItem
                      key={`warehouse-${option._id || option.warehouseId || option.description}`}
                      value={`warehouse:${option._id || option.warehouseId}`}
                    >
                      {option.description || t('yard.move.warehouseName', { id: option.warehouseId || option._id })}
                    </MenuItem>
                  ))}
                  {allYards.length === 0 && warehouses.length === 0 && (
                    <MenuItem value="" disabled>
                      {t('yard.move.noDestinations')}
                    </MenuItem>
                  )}
                </TextField>
                <TextField
                  select
                  label={t('yard.move.boxType')}
                  fullWidth
                  margin="dense"
                  value={moveBoxType}
                  onChange={event => setMoveBoxType(event.target.value as 'deeps' | 'super' | 'shallow')}
                >
                  <MenuItem value="deeps">{t('inventory.boxTypes.deeps')}</MenuItem>
                  <MenuItem value="super">{t('inventory.boxTypes.super')}</MenuItem>
                  <MenuItem value="shallow">{t('inventory.boxTypes.shallow')}</MenuItem>
                </TextField>
                <TextField
                  label={t('yard.move.hives')}
                  type="number"
                  fullWidth
                  margin="dense"
                  value={moveQuantity}
                  onChange={event => setMoveQuantity(event.target.value)}
                />
                <TextField
                  label={t('yard.move.date')}
                  type="date"
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  value={moveDate}
                  onChange={event => setMoveDate(event.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setMoveOpen(false)}>
                  {t('button.cancel.text')}
                </Button>
                <Button variant="contained" onClick={handleSaveMovement}>
                  {t('button.save.text')}
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog open={harvestOpen} onClose={() => setHarvestOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>{t('yard.actions.harvest')}</DialogTitle>
              <DialogContent>
                <TextField
                  select
                  label={t('yard.harvest.honeyType')}
                  fullWidth
                  margin="dense"
                  value={harvestHoneyType}
                  onChange={event => setHarvestHoneyType(event.target.value)}
                >
                  <MenuItem value="avocado">{t('inventory.honeyTypes.avocado')}</MenuItem>
                  <MenuItem value="clover">{t('inventory.honeyTypes.clover')}</MenuItem>
                  <MenuItem value="orangeBlossom">{t('inventory.honeyTypes.orangeBlossom')}</MenuItem>
                  <MenuItem value="wildflower">{t('inventory.honeyTypes.wildflower')}</MenuItem>
                </TextField>
                <TextField
                  select
                  label={t('yard.harvest.format')}
                  fullWidth
                  margin="dense"
                  value={harvestFormat}
                  onChange={event => setHarvestFormat(event.target.value)}
                >
                  <MenuItem value="barrel">{t('inventory.barrel')}</MenuItem>
                  <MenuItem value="combHoney">{t('inventory.combHoney')}</MenuItem>
                </TextField>
                <TextField
                  label={t('yard.harvest.amount')}
                  type="number"
                  fullWidth
                  margin="dense"
                  value={harvestQuantity}
                  onChange={event => setHarvestQuantity(event.target.value)}
                />
                <TextField
                  label={t('yard.harvest.date')}
                  type="date"
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  value={harvestDate}
                  onChange={event => setHarvestDate(event.target.value)}
                />
                <TextField
                  label={t('yard.harvest.notes')}
                  fullWidth
                  margin="dense"
                  multiline
                  minRows={3}
                  value={harvestNotes}
                  onChange={event => setHarvestNotes(event.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setHarvestOpen(false)}>
                  {t('button.cancel.text')}
                </Button>
                <Button variant="contained" onClick={handleSaveHarvest}>
                  {t('button.save.text')}
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="body1">{t('yard.notFound')}</Typography>
      )}
    </Container>
  )
}

export default YardDetailsScreen
